// 回放時間軸（SPEC #vms-web-timeline PLAY-01，驗收 W21）：播放頭固定在中線、拖曳捲動 + 慣性、滾輪 / 捏合六級縮放（游標為錨點）、
// 鍵盤（← / → 一格、Shift + ← / → 上下一個事件、Home / End、空白鍵、+ / -）、事件色塊 + 類別圖示（顏色與圖示跟 App 同一組）、
// 拖曳 / 滑過縮圖、四種缺口底紋、「只看有事件的時段」的折縫。純邏輯在 timeline.ts。
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { GapReason, RecordingSource, TimelineGap, TimelineSpan } from '@kinio/web-sdk';
import { eventDensity } from './calendar';
import {
  EVENT_GROUP_STYLE,
  THUMB_DRAG_INTERVAL_MS,
  THUMB_HOVER_DELAY_MS,
  TIMELINE_EVENT_PREROLL_S,
  TimelineScrubber,
  adjacentEvent,
  axisTicks,
  inertiaTarget,
  mergeMarkers,
  nearestLevel,
  stepLevel,
  tickSteps,
  tickWithSeconds,
  toReal,
  toVirt,
  virtBounds,
  virtRanges,
  zoomCenter,
  type Axis,
  type EventGroup,
} from './timeline';

type T = (k: string, o?: Record<string, unknown>) => string;

export interface TimelineEvent {
  /** 真實時間（秒） */
  ts: number;
  group: EventGroup;
  id: string;
  title: string;
}

export interface TimelineRowData {
  key: string;
  label: string;
  spans: TimelineSpan[];
  gaps: TimelineGap[];
}

interface Props {
  axis: Axis;
  /** 現在（秒）；中線不能超過 */
  now: number;
  /** 播放中的時間（真實秒）；沒在播 = null。中線跟著走（拖曳 / 等 seek 時不跟） */
  playhead: number | null;
  /** 一開始中線放在哪（真實秒） */
  initialCenter: number;
  /** 一開始的可視範圍（秒；預設當天） */
  initialSpan?: number;
  /** 第一列是合併時間軸，其他是「檢視全部來源」 */
  rows: TimelineRowData[];
  events: TimelineEvent[];
  /** 事件抓到上限還有更多（標記上的數字寫「N+」） */
  eventsCapped?: boolean;
  /** 當天 / 隔天的日期（跨午夜的分隔線標籤） */
  dayLabels: [string, string];
  fmt: (sec: number, withSeconds?: boolean) => string;
  gapText: (g: TimelineGap) => string;
  srcText: (s: RecordingSource) => string;
  t: T;
  /** 點一下時吸附到附近的短片段（容差 = 6 px 換成秒） */
  snap?: (at: number, tolSec: number) => number;
  /** 從真實時間 at 播放（同一來源內 seek、跨來源由呼叫端換來源） */
  onSeek(at: number): void;
  onTogglePlay(): void;
  /** 中線變了（真實秒；書籤 / 匯出 / 切換「只看有事件」時用） */
  onCenter?(at: number): void;
  /** 要縮圖：拖曳中的中線或滑過的游標（真實秒）；null = 不要了 */
  onPeek?(at: number | null): void;
  /** 縮圖（呼叫端依來源拿：雲端片段縮圖 / 本地 pb.thumb） */
  peek?: { at: number; url: string } | null;
  /** 頁首右側的控制項（時區等） */
  toolbar?: ReactNode;
}

const CLICK_PX = 4;

export function PlaybackTimeline(p: Props) {
  const { axis, t } = p;
  const trackRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  const bounds = useMemo(() => virtBounds(axis, p.now), [axis, p.now]);
  const [span, setSpan] = useState(() => p.initialSpan ?? axis.length);
  const scrub = useRef<TimelineScrubber | null>(null);
  if (!scrub.current) scrub.current = new TimelineScrubber(toVirt(axis, p.initialCenter), { ...bounds, width, spanS: span });
  const [center, setCenterState] = useState(() => scrub.current!.center);
  const [dragging, setDragging] = useState(false);
  const [peekX, setPeekX] = useState<number | null>(null);
  const seekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const glideRaf = useRef<number | null>(null);
  const gesture = useRef<{ id: number; x: number; moved: boolean; samples: { t: number; x: number }[] } | null>(null);
  const pinch = useRef<{ pts: Map<number, number>; d0: number; span0: number; anchor: number; frac: number } | null>(null);
  const wheelAcc = useRef(0);
  const snapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPeek = useRef(0);
  const pRef = useRef(p);
  pRef.current = p;

  const dayLen = axis.collapsed ? Math.max(60, axis.length) : axis.length;
  const minSpan = 60;
  const maxSpan = Math.max(minSpan, dayLen);
  const sPerPx = span / Math.max(1, width);
  const from = center - span / 2;
  const pctOf = (v: number) => ((v - from) / span) * 100;

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => setWidth(el.clientWidth || 900)) : null;
    setWidth(el.clientWidth || 900);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  useEffect(() => {
    scrub.current!.setBounds({ ...bounds, width, spanS: span });
    setCenterState(scrub.current!.center);
  }, [bounds, width, span]);

  const setCenter = (v: number) => {
    setCenterState(v);
    pRef.current.onCenter?.(toReal(axis, v));
  };

  // 播放中中線跟著播放時間
  useEffect(() => {
    if (p.playhead === null) return;
    const s = scrub.current!;
    const before = s.center;
    s.follow(toVirt(axis, p.playhead));
    if (s.center !== before) setCenterState(s.center);
  }, [p.playhead, axis]);

  useEffect(
    () => () => {
      for (const tm of [seekTimer.current, snapTimer.current, peekTimer.current]) if (tm) clearTimeout(tm);
      if (glideRaf.current !== null) cancelAnimationFrame(glideRaf.current);
    },
    [],
  );

  /** 排定的 seek 到時間就送（放開 / 鍵盤之後 300 ms 內沒再動）。 */
  function armSeek() {
    if (seekTimer.current) clearTimeout(seekTimer.current);
    const at = scrub.current!.pendingAt;
    if (at === null) return;
    seekTimer.current = setTimeout(
      () => {
        const c = scrub.current!.due(performance.now());
        if (c !== null) pRef.current.onSeek(toReal(axis, c));
      },
      Math.max(0, at - performance.now()),
    );
  }

  function peekAt(v: number | null, xPct: number, mode: 'drag' | 'hover') {
    const onPeek = pRef.current.onPeek;
    if (!onPeek) return;
    if (peekTimer.current) clearTimeout(peekTimer.current);
    if (v === null) {
      setPeekX(null);
      onPeek(null);
      return;
    }
    const fire = () => {
      lastPeek.current = performance.now();
      setPeekX(xPct);
      onPeek(toReal(axis, v));
    };
    if (mode === 'drag') {
      const wait = THUMB_DRAG_INTERVAL_MS - (performance.now() - lastPeek.current);
      if (wait <= 0) fire();
      else peekTimer.current = setTimeout(fire, wait);
    } else {
      peekTimer.current = setTimeout(fire, THUMB_HOVER_DELAY_MS);
    }
  }

  function zoomTo(newSpan: number, anchor = center, frac = 0.5) {
    const s = Math.min(maxSpan, Math.max(minSpan, newSpan));
    setSpan(s);
    const sc = scrub.current!;
    sc.setBounds({ ...bounds, width, spanS: s });
    if (!sc.dragging) {
      // 縮放不 seek：只移中線（錨點留在同一個位置）
      const c = Math.min(bounds.now, Math.max(0, zoomCenter(anchor, frac, s)));
      if (sc.pendingAt === null) sc.reset(c);
      else sc.center = c;
      setCenter(sc.center);
    }
  }

  function jump(v: number) {
    const c = scrub.current!.jump(performance.now(), v);
    setCenter(c);
    armSeek();
  }

  // ---- 指標：拖曳 / 點擊 / 兩指捏合 ----
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if ((e.target as HTMLElement).closest('[data-marker], button')) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (glideRaf.current !== null) {
      cancelAnimationFrame(glideRaf.current);
      glideRaf.current = null;
    }
    if (e.pointerType === 'touch' && gesture.current) {
      // 第二根手指：改成捏合縮放
      const r = e.currentTarget.getBoundingClientRect();
      const pts = new Map([[gesture.current.id, gesture.current.x], [e.pointerId, e.clientX]]);
      const xs = [...pts.values()];
      const mid = (xs[0] + xs[1]) / 2;
      const frac = (mid - r.left) / r.width;
      pinch.current = { pts, d0: Math.max(10, Math.abs(xs[0] - xs[1])), span0: span, anchor: from + frac * span, frac };
      gesture.current = null;
      scrub.current!.up(performance.now());
      scrub.current!.reset(scrub.current!.center);
      setDragging(false);
      return;
    }
    gesture.current = { id: e.pointerId, x: e.clientX, moved: false, samples: [{ t: performance.now(), x: e.clientX }] };
    scrub.current!.down(performance.now());
    if (seekTimer.current) clearTimeout(seekTimer.current);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    if (pinch.current) {
      const pc = pinch.current;
      if (!pc.pts.has(e.pointerId)) return;
      pc.pts.set(e.pointerId, e.clientX);
      const xs = [...pc.pts.values()];
      const d = Math.max(10, Math.abs(xs[0] - xs[1]));
      const s = Math.min(maxSpan, Math.max(minSpan, (pc.span0 * pc.d0) / d));
      setSpan(s);
      const c = Math.min(bounds.now, Math.max(0, zoomCenter(pc.anchor, pc.frac, s)));
      scrub.current!.reset(c);
      setCenter(c);
      return;
    }
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) {
      if (e.pointerType === 'mouse') peekAt(from + frac * span, frac * 100, 'hover');
      return;
    }
    const dx = e.clientX - g.x;
    if (!g.moved && Math.abs(dx) < CLICK_PX) return;
    if (!g.moved) {
      g.moved = true;
      setDragging(true);
    }
    g.samples.push({ t: performance.now(), x: e.clientX });
    if (g.samples.length > 20) g.samples.shift();
    const c = scrub.current!.move(performance.now(), dx);
    setCenter(c);
    peekAt(c, 50, 'drag');
  }

  function endGesture(e: React.PointerEvent<HTMLDivElement>) {
    if (pinch.current) {
      pinch.current.pts.delete(e.pointerId);
      if (pinch.current.pts.size < 2) {
        const pc = pinch.current;
        pinch.current = null;
        zoomTo(nearestLevel(span, dayLen), pc.anchor, pc.frac); // 捏合放開吸附到最近一級
      }
      return;
    }
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    gesture.current = null;
    const sc = scrub.current!;
    if (!g.moved) {
      // 點一下：中線移到點的位置，立刻播
      const r = e.currentTarget.getBoundingClientRect();
      let v = from + ((e.clientX - r.left) / r.width) * span;
      if (p.snap) v = toVirt(axis, p.snap(toReal(axis, v), 6 * sPerPx)); // 24 小時視窗裡 15 秒的片段不到 1 像素
      sc.up(performance.now());
      sc.reset(v);
      setCenter(sc.center);
      pRef.current.onSeek(toReal(axis, sc.center));
      return;
    }
    setDragging(false);
    peekAt(null, 0, 'drag');
    const target = inertiaTarget(g.samples, performance.now(), sc.center, { ...bounds, width, spanS: span });
    if (target === null || Math.abs(target - sc.center) < sPerPx) {
      sc.up(performance.now());
      armSeek();
      return;
    }
    // 慣性：400 ms 減速捲到 target（遇到「現在」已經被 clamp 停住），停下來才排 seek
    const c0 = sc.center;
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / 400);
      const c = sc.glide(c0 + (target - c0) * (1 - Math.pow(1 - k, 3)));
      setCenter(c);
      if (k < 1) glideRaf.current = requestAnimationFrame(step);
      else {
        glideRaf.current = null;
        sc.up(performance.now());
        armSeek();
      }
    };
    glideRaf.current = requestAnimationFrame(step);
  }

  // ---- 滾輪：一格一級；觸控板捏合（ctrlKey）連續縮放、停 150 ms 吸附；水平捲動 = 拖曳 ----
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const st = stateRef.current;
      const anchor = st.from + frac * st.span;
      if (e.ctrlKey) {
        const s = Math.min(st.maxSpan, Math.max(minSpan, st.span * Math.exp(e.deltaY * 0.01)));
        st.zoomTo(s, anchor, frac);
        if (snapTimer.current) clearTimeout(snapTimer.current);
        snapTimer.current = setTimeout(() => {
          const cur = stateRef.current;
          cur.zoomTo(nearestLevel(cur.span, cur.dayLen), anchor, frac);
        }, 150);
        return;
      }
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        st.jump(st.center + e.deltaX * st.sPerPx);
        return;
      }
      wheelAcc.current += e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
      if (Math.abs(wheelAcc.current) < 50) return;
      const dir: 1 | -1 = wheelAcc.current < 0 ? 1 : -1;
      wheelAcc.current = 0;
      st.zoomTo(stepLevel(st.span, st.dayLen, dir), anchor, frac);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // 鍵盤（時間軸聚焦時）
  const eventStarts = useMemo(() => p.events.map((e) => toVirt(axis, e.ts)), [p.events, axis]);
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const { minor } = tickSteps(span);
    const sc = scrub.current!;
    const k = e.key;
    if (k === 'ArrowLeft' || k === 'ArrowRight') {
      const dir = k === 'ArrowRight' ? 1 : -1;
      if (e.shiftKey) {
        const ev = adjacentEvent(eventStarts, sc.center + TIMELINE_EVENT_PREROLL_S, dir);
        if (ev !== null) jump(Math.max(0, ev - TIMELINE_EVENT_PREROLL_S));
      } else jump(sc.center + dir * minor);
    } else if (k === 'Home') jump(0);
    else if (k === 'End') jump(bounds.now);
    else if (k === ' ' || k === 'Spacebar') p.onTogglePlay();
    else if (k === '+' || k === '=') zoomTo(stepLevel(span, dayLen, 1));
    else if (k === '-' || k === '_') zoomTo(stepLevel(span, dayLen, -1));
    else return;
    e.preventDefault();
  }

  const stateRef = useRef({ from, span, center, sPerPx, dayLen, maxSpan, zoomTo, jump });
  stateRef.current = { from, span, center, sPerPx, dayLen, maxSpan, zoomTo, jump };

  // ---- 畫 ----
  const markers = useMemo(() => {
    const vs = p.events.map((e) => ({ start: toVirt(axis, e.ts), group: e.group, id: e.id }));
    return mergeMarkers(vs, { width, spanS: span }).map((m) => {
      const real = toReal(axis, m.start);
      return { ...m, real, seekAt: Math.max(axis.dayStart, real - TIMELINE_EVENT_PREROLL_S) };
    });
  }, [p.events, axis, width, span]);
  const evById = useMemo(() => new Map(p.events.map((e) => [e.id, e])), [p.events]);
  const ticks = axisTicks(axis, center, span);
  const withSec = tickWithSeconds(span);
  const realCenter = toReal(axis, center);
  const density = eventDensity(eventStarts, from, span, 96);

  const rect = (a: number, b: number) => {
    const l = Math.max(0, pctOf(a));
    const r = Math.min(100, pctOf(b));
    return r > l ? { left: `${l}%`, width: `${Math.max(r - l, 0.15)}%` } : null;
  };

  return (
    <div className="pb-tl">
      <div className="pb-zoom">
        <button className="btn sm" onClick={() => jump(center - span / 2)} disabled={center <= 0} aria-label={t('playback.pan_back')}>
          ◀
        </button>
        <button className="btn sm" onClick={() => zoomTo(stepLevel(span, dayLen, -1))} disabled={span >= maxSpan} aria-label={t('playback.zoom_out')}>
          −
        </button>
        <span className="muted" data-testid="pb-zoom-span">
          {t('playback.zoom_span', { span: spanLabel(span, t) })}
        </span>
        <button className="btn sm" onClick={() => zoomTo(stepLevel(span, dayLen, 1))} disabled={span <= minSpan} aria-label={t('playback.zoom_in')}>
          +
        </button>
        <button className="btn sm" onClick={() => jump(center + span / 2)} disabled={center >= bounds.now} aria-label={t('playback.pan_forward')}>
          ▶
        </button>
        {p.toolbar}
      </div>
      <div className="pb-tl-grid">
        <div className="pb-tl-labels" aria-hidden>
          <span className="pb-row-label thin">{t('playback.event_density')}</span>
          {p.rows.map((r, i) => (
            <span key={r.key} className={`pb-row-label ${i ? 'thin' : 'primary'}`}>
              {r.label}
            </span>
          ))}
        </div>
        <div
          ref={trackRef}
          className={`pb-tl-tracks ${dragging ? 'dragging' : ''}`}
          role="slider"
          tabIndex={0}
          aria-label={t('playback.timeline_aria')}
          aria-valuemin={toReal(axis, 0)}
          aria-valuemax={toReal(axis, bounds.now)}
          aria-valuenow={Math.round(realCenter)}
          aria-valuetext={p.fmt(realCenter, true)}
          data-testid="pb-timeline"
          data-center={Math.round(realCenter)}
          data-span={span}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse' && !gesture.current) peekAt(null, 0, 'hover');
          }}
          onKeyDown={onKeyDown}
        >
          <div className="pb-density" data-testid="pb-density">
            {density.map((b, i) => (
              <span key={i} style={{ opacity: b.n ? 0.25 + 0.75 * b.level : 0 }} title={b.n ? t('playback.events_n', { count: b.n }) : undefined} data-n={b.n || undefined} />
            ))}
          </div>
          {p.rows.map((row, ri) => (
            <div key={row.key} className={`pb-timeline ${ri ? 'thin' : 'primary'}`} data-row={row.key}>
              {!axis.collapsed && from < 0 && <span className="pb-outday" style={rect(from, 0) ?? undefined} />}
              {!axis.collapsed && from + span > axis.length && <span className="pb-outday" style={rect(axis.length, from + span) ?? undefined} />}
              {from + span > bounds.now && bounds.now < axis.length && <span className="pb-future" style={rect(bounds.now, Math.min(axis.length, from + span)) ?? undefined} />}
              {row.gaps.flatMap((g, i) =>
                virtRanges(axis, g.start, g.end).map(([a, b], j) => {
                  const st = rect(a, b);
                  return st ? <span key={`g${i}.${j}`} className={`gap gap-${g.reason as GapReason}`} data-gap={g.reason} style={st} title={p.gapText(g)} /> : null;
                }),
              )}
              {row.spans.flatMap((s, i) =>
                virtRanges(axis, s.start, s.end).map(([a, b], j) => {
                  const st = rect(a, b);
                  return st ? (
                    <span
                      key={`s${i}.${j}`}
                      className={`seg src-${s.source} ${s.event ? 'evt' : ''}`}
                      style={st}
                      title={`${p.fmt(s.start)} – ${p.fmt(s.end)} · ${p.srcText(s.source)}${s.event ? ' · ' + t('playback.type_event') : ''}`}
                    />
                  ) : null;
                }),
              )}
              {ri === 0 &&
                axis.seams.map((sm, i) => {
                  const x = pctOf(sm.v);
                  return x >= 0 && x <= 100 ? (
                    <span key={`m${i}`} className="pb-seam" data-seam style={{ left: `${x}%` }} title={t('playback.seam', { d: durLabel(sm.to - sm.from, t) })}>
                      <span className="small">{durLabel(sm.to - sm.from, t)}</span>
                    </span>
                  ) : null;
                })}
              {ri === 0 &&
                markers.map((m, i) => {
                  const x0 = pctOf(m.start);
                  const x1 = pctOf(m.end);
                  if (x1 < -2 || x0 > 102) return null;
                  const st = EVENT_GROUP_STYLE[m.group];
                  const first = evById.get(m.ids[0]);
                  // 抓到上限時，包含全部事件的那一群寫「1000+」，不寫成剛好 1000（第七輪 R7-P2-6）
                  const capped = !!p.eventsCapped && m.count >= p.events.length;
                  const label = m.count > 1 ? t(capped ? 'playback.events_n_more' : 'playback.events_n', { count: m.count }) : (first?.title ?? t(`playback.group_${m.group}`));
                  return (
                    <button
                      key={`e${i}`}
                      type="button"
                      tabIndex={-1}
                      className={`pb-marker ${m.count > 1 ? 'multi' : ''}`}
                      data-marker={m.group}
                      data-count={m.count}
                      data-seek={m.seekAt}
                      style={{ left: `${x0}%`, width: m.count > 1 ? `max(${x1 - x0}%, 14px)` : undefined, background: st.color }}
                      title={`${p.fmt(m.real)} · ${label}`}
                      aria-label={`${p.fmt(m.real)} ${label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const sc = scrub.current!;
                        sc.reset(toVirt(axis, m.seekAt));
                        setCenter(sc.center);
                        p.onSeek(m.seekAt); // 從事件開始前 3 秒播
                      }}
                    >
                      <span aria-hidden>{st.icon}</span>
                      {m.count > 1 && <span className="n">{capped ? `${m.count}+` : m.count}</span>}
                    </button>
                  );
                })}
            </div>
          ))}
          <div className="pb-ticks">
            {ticks
              .filter((tk) => tk.major)
              .map((tk) => (
                <span key={tk.t} style={{ left: `${pctOf(tk.v)}%`, transform: edgeShift(pctOf(tk.v)) }}>
                  {p.fmt(tk.t, withSec)}
                </span>
              ))}
            {!axis.collapsed &&
              ([0, axis.length] as const).map((v, i) => {
                const x = pctOf(v);
                return x > 0 && x < 100 ? (
                  <span key={`d${i}`} className="pb-daysep" style={{ left: `${x}%` }}>
                    {p.dayLabels[i]}
                  </span>
                ) : null;
              })}
          </div>
          {ticks.map((tk) => (
            <span key={`tk${tk.t}`} className={`pb-tick ${tk.major ? 'major' : ''}`} style={{ left: `${pctOf(tk.v)}%` }} />
          ))}
          <span className="pb-playhead" aria-hidden>
            <span className="pb-playhead-time">{p.fmt(realCenter, withSec || span <= 900)}</span>
          </span>
          {p.peek && peekX !== null && (
            <div
              className="pb-hover-thumb"
              // 靠右緣時往左翻（自身寬度按同一個百分比位移），不讓整頁出現水平捲軸（第二輪 B2-11）
              style={{ left: `${Math.min(100, Math.max(0, peekX))}%`, transform: `translateX(-${Math.min(100, Math.max(0, peekX))}%)` }}
              data-testid="pb-hover-thumb"
            >
              <img src={p.peek.url} alt="" />
              <span className="small">{p.fmt(p.peek.at, true)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** 靠邊的刻度標籤往內收，不被裁掉。 */
function edgeShift(x: number): string {
  return x < 3 ? 'translateX(0)' : x > 97 ? 'translateX(-100%)' : 'translateX(-50%)';
}

function spanLabel(sec: number, t: T): string {
  return sec >= 3600 ? t('playback.hours', { n: Math.round(sec / 360) / 10 }) : t('playback.minutes', { n: Math.round(sec / 60) });
}

function durLabel(sec: number, t: T): string {
  const total = Math.round(sec / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return t('playback.dur_hm', { h, m });
  if (h) return t('playback.hours', { n: h });
  return t('playback.minutes', { n: Math.max(1, m) });
}
